import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, animate } from "framer-motion";

// ---- Top-Recruit chart palette (actual color strings for recharts / inline styles) ----
export const CHART = {
  blue: "hsl(200 100% 28%)",
  blueLt: "hsl(200 100% 40%)",
  cyan: "hsl(198 100% 42%)",
  green: "hsl(150 100% 30%)",
  purple: "hsl(280 29% 52%)",
  amber: "#d97706",
  rose: "#e11d48",
  slate: "#94a3b8",
};

// Graduated deep-blue -> teal progression for the pipeline stages (no rainbow).
export const PIPELINE_SHADES = [
  "hsl(202 100% 22%)", "hsl(200 100% 27%)", "hsl(198 95% 32%)",
  "hsl(194 88% 36%)", "hsl(188 82% 38%)", "hsl(180 78% 37%)",
  "hsl(170 74% 35%)", "hsl(160 72% 33%)", "hsl(150 70% 31%)",
];

export const TONE = {
  blue: "bg-brand/10 text-brand", green: "bg-emerald-50 text-emerald-600", purple: "bg-purple-50 text-purple-600",
  amber: "bg-amber-50 text-amber-600", red: "bg-rose-50 text-rose-600", gray: "bg-slate-100 text-slate-500",
};
export const REASON_TONE = {
  blocked: "bg-rose-50 text-rose-700", overdue_task: "bg-rose-50 text-rose-700",
  ready_for_review: "bg-emerald-50 text-emerald-700", action_needed: "bg-amber-50 text-amber-800",
  unassigned: "bg-slate-100 text-slate-600", inactivity: "bg-amber-50 text-amber-800",
};

export function Panel({ title, icon: Icon, action, children, testId, className = "", index = 0 }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3), ease: "easeOut" }}
      className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`} data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        {Icon && <Icon className="w-4 h-4 text-brand/70" />}
        <h3 className="text-sm font-semibold text-slate-800 flex-1">{title}</h3>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </motion.section>
  );
}

export function Skeleton({ h = "h-24" }) {
  return <div className={`rounded-xl border border-slate-200 bg-white ${h} animate-pulse`} />;
}

// Compact, honest positive/neutral empty state (no oversized dashed container).
export function MiniEmpty({ icon: Icon, title, description, positive = false, testId = "mini-empty" }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-slate-50/70 border border-slate-100 px-3.5 py-3" data-testid={testId}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${positive ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-700 leading-tight">{title}</p>
        {description && <p className="text-xs text-slate-400 leading-tight mt-0.5">{description}</p>}
      </div>
    </div>
  );
}

// Counts in on first load (0 -> value) and eases from the previous value on updates
// (so filter changes transition smoothly rather than replaying from zero). Respects reduced motion.
export function CountUp({ value = 0, className = "", testId }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    const v = Number(value) || 0;
    const from = prev.current;
    prev.current = v;
    if (reduce || from === v) { setDisplay(v); return; }
    const controls = animate(from, v, { duration: 0.7, ease: "easeOut", onUpdate: (n) => setDisplay(Math.round(n)) });
    return () => controls.stop();
  }, [value, reduce]);
  return <span className={className} data-testid={testId}>{display}</span>;
}

export const CT_LABELS = { retail_traditional: "Retail Traditional", retail_acquisition: "Retail Acquisition", associate_agent: "Associate", protege: "Protégé", producer: "Producer", staff: "Staff" };
export function ctLabel(k) { return CT_LABELS[k] || k || "—"; }
export function monthLabel(m) {
  if (!m) return "";
  const [, mo] = m.split("-");
  return ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(mo)] || m;
}
export function fmtDay(d) {
  if (!d) return "";
  const dt = new Date(d); const today = new Date();
  if (dt.toDateString() === today.toDateString()) return dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return dt.toLocaleDateString([], { month: "short", day: "numeric" });
}
